import { useNavigate, Navigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { platformRoles } from "@/data/platformData";
import { MDRBadge } from "@/components/MDRBadge";
import {
  Stethoscope, Building2, User, ClipboardList, Hospital, Settings,
  LogOut, ArrowRight, ShieldCheck
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

const iconMap = {
  Stethoscope, Building2, User, ClipboardList, Hospital, Settings
};

export const ProfileSelectorPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, setActiveRole, logout } = useAppContext();

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const handleSelectRole = (role) => {
    setActiveRole(role);
    navigate(role.targetPath);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-background to-muted">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex h-16 items-center justify-between px-4 md:px-8">
          <img src={LOGO_URL} alt="WitApp" className="h-8 md:h-10 w-auto" />
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium leading-tight">{currentUser?.name}</p>
              <p className="text-xs text-muted-foreground">{currentUser?.email}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="profile-selector-logout-btn"
              className="gap-2 text-destructive hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 md:px-8 pt-10 md:pt-16 pb-6 max-w-7xl mx-auto">
        <div className="flex flex-col gap-3 animate-fade-in-up">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-primary" />
            <span>Piattaforma di Telemedicina · Multi-tenant</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Benvenuto{currentUser?.name ? `, ${currentUser.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Seleziona il profilo con cui vuoi operare. Ogni profilo apre un workspace dedicato,
            con regole di accesso e funzionalità conformi al perimetro MDR.
          </p>
        </div>
      </section>

      {/* Profile cards */}
      <section className="px-4 md:px-8 pb-10 max-w-7xl mx-auto">
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {platformRoles.map((role, idx) => {
            const Icon = iconMap[role.icon] || User;
            return (
              <Card
                key={role.id}
                onClick={() => handleSelectRole(role)}
                data-testid={`profile-card-${role.id}`}
                className="group cursor-pointer overflow-hidden border-2 hover:border-primary/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl animate-fade-in-up"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                {/* Color band */}
                <div className={`h-2 bg-gradient-to-r ${role.color}`} />
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${role.color} text-white shadow-lg shadow-black/10`}>
                      <Icon className="h-7 w-7" />
                    </div>
                    <MDRBadge variant={role.mdrCategory} size="sm" />
                  </div>

                  <div className="space-y-1.5">
                    <h3 className="text-lg font-semibold leading-tight">{role.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{role.description}</p>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-muted-foreground font-mono">{role.targetPath}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="gap-1.5 text-primary group-hover:gap-2.5 transition-all"
                      data-testid={`profile-enter-${role.id}-btn`}
                    >
                      Entra
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer info bar */}
        <div className="mt-10 grid gap-3 sm:grid-cols-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2 p-3 rounded-lg bg-card border">
            <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>GDPR · NIS2 · ISO 13485 · UNI EN 301549</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-card border">
            <Hospital className="h-4 w-4 text-rose-600 shrink-0" />
            <span>Componenti MDR Classe IIa marcati CE (refertazione, viewer ECG/spiro)</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-lg bg-card border">
            <Building2 className="h-4 w-4 text-indigo-600 shrink-0" />
            <span>Architettura cloud multi-tenant a microservizi</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfileSelectorPage;
